import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

interface ClassSpec {
  class: string
  label: string
  description: string
  basis: string
}

interface RegionSpec {
  code: string
  label: string
  zone: string
  note: string
}

interface ResidencyRule {
  class: string
  label: string
  description: string
  allowed_regions: string[]
  required_basis: string
  basis_default: string
  updated_at?: string
}

interface ResidencyCatalogue {
  regulation: string
  classes: ClassSpec[]
  regions: RegionSpec[]
}

interface Placement {
  class: string
  region: string
  allowed: boolean
  reason: string
  checked_at: string
}

const RULES_KEY = ['residency-rules']
const CATALOGUE_KEY = ['residency-catalogue']

export function Residency() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [draftRegions, setDraftRegions] = useState<string[]>([])
  const [draftBasis, setDraftBasis] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [checkClass, setCheckClass] = useState('personal_data')
  const [checkRegion, setCheckRegion] = useState('US')
  const [placement, setPlacement] = useState<Placement | null>(null)

  const catalogue = useQuery({
    queryKey: CATALOGUE_KEY,
    queryFn: () => api<ResidencyCatalogue>('/v1/residency/catalogue'),
    staleTime: 5 * 60_000,
  })

  const rules = useQuery({
    queryKey: RULES_KEY,
    queryFn: () => list<ResidencyRule>('/v1/residency/rules'),
    staleTime: 15_000,
  })

  const rows = rules.data ?? []
  const classes = catalogue.data?.classes ?? []
  const regions = catalogue.data?.regions ?? []
  const active = rows.find((row) => row.class === selected) ?? rows[0]

  // Reset the editor when the selected class changes, or when a save returns a new timestamp.
  useEffect(() => {
    if (!active) return
    setDraftRegions(active.allowed_regions)
    setDraftBasis(active.required_basis)
  }, [active?.class, active?.updated_at])

  const save = useMutation({
    mutationFn: (value: { class: string; regions: string[]; basis: string }) =>
      apiWrite<ResidencyRule>(`/v1/residency/rules/${encodeURIComponent(value.class)}`, 'PUT', {
        allowed_regions: value.regions,
        required_basis: value.basis,
      }),
    onSuccess: (row) => {
      qc.setQueryData<ResidencyRule[]>(RULES_KEY, (old) => (old
        ? old.map((item) => (item.class === row.class ? row : item))
        : [row]))
      setError(null)
      setNotice(`บันทึกกฎของ${row.label}แล้ว`)
    },
    onError: (e) => {
      setNotice(null)
      setError(e instanceof Error ? e.message : 'บันทึกกฎไม่สำเร็จ')
    },
  })

  const check = useMutation({
    mutationFn: (value: { class: string; region: string }) =>
      api<Placement>(`/v1/residency/check?class=${encodeURIComponent(value.class)}&region=${encodeURIComponent(value.region)}`),
    onSuccess: (row) => setPlacement(row),
    onError: () => setPlacement(null),
  })

  const currentRegions = [...(active?.allowed_regions ?? [])].sort().join('|')
  const nextRegions = [...draftRegions].sort().join('|')
  const dirty = active
    ? nextRegions !== currentRegions || draftBasis.trim() !== active.required_basis
    : false

  function toggleRegion(code: string) {
    setNotice(null)
    setDraftRegions((prev) => (prev.includes(code)
      ? prev.filter((region) => region !== code)
      : [...prev, code]))
  }

  function resetDraft() {
    if (!active) return
    setDraftRegions(active.allowed_regions)
    setDraftBasis(active.required_basis)
    setError(null)
    setNotice(null)
  }

  function commit() {
    if (!active || draftRegions.length === 0) return
    save.mutate({ class: active.class, regions: draftRegions, basis: draftBasis.trim() })
  }

  return (
    <>
      <PageHead
        title="ถิ่นที่อยู่ของข้อมูล"
        sub="กำหนดว่าข้อมูลแต่ละประเภทเก็บในภูมิภาคใดได้บ้าง ตาม PDPA มาตรา 28 และ 29 · ค่าเริ่มต้นคือไทย และระบบจะปฏิเสธภูมิภาคที่ไม่มีในกฎ"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} ประเภทข้อมูล</span>
        <span className="chip">{regions.length} ภูมิภาคที่รองรับ</span>
        <span className="chip">ค่าเริ่มต้น: ไทย</span>
        <span className="chip">ไม่ตรงกฎ = ปฏิเสธ</span>
      </div>

      {rules.isLoading || catalogue.isLoading ? (
        <div className="stack"><Skel h={58} /><Skel h={58} /><Skel h={58} /><Skel h={58} /></div>
      ) : rules.isError || catalogue.isError ? (
        <div className="card card-pad">
          <div className="form-error">โหลดกฎถิ่นที่อยู่ไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>ประเภทข้อมูล</th>
                <th>ภูมิภาคที่อนุญาต</th>
                <th>ฐานกฎหมายที่ต้องใช้</th>
                <th style={{ width: 84 }} aria-label="แก้ไข" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.class}
                  onClick={() => { setSelected(row.class); setNotice(null) }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <div style={{ color: 'var(--color-ivory)' }}>{row.label || row.class}</div>
                    <div className="mono dim" style={{ fontSize: 11, marginTop: 3 }}>{row.class}</div>
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 4, maxWidth: 360, lineHeight: 1.6 }}>
                      {row.description}
                    </div>
                  </td>
                  <td>
                    {row.allowed_regions.length === 0 ? (
                      <span className="chip" style={{ color: 'var(--color-crimson)' }}>ยังไม่มีภูมิภาค</span>
                    ) : (
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {row.allowed_regions.map((code) => {
                          const spec = regions.find((region) => region.code === code)
                          return (
                            <span key={code} className="chip">
                              {spec?.label ?? code} · {code}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </td>
                  <td className="dim" style={{ fontSize: 11.5, lineHeight: 1.7, maxWidth: 330, overflowWrap: 'anywhere' }}>
                    {row.required_basis || row.basis_default}
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost"
                      aria-label={`แก้ไขภูมิภาคของ ${row.label || row.class}`}
                      onClick={(event) => { event.stopPropagation(); setSelected(row.class); setNotice(null) }}
                    >
                      แก้ไข
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error ? <div className="form-error" style={{ marginTop: 14 }}>{error}</div> : null}

      {active ? (
        <div className="card card-pad" style={{ marginTop: 22 }}>
          <div className="row" style={{ gap: 12 }}>
            <div className="card-label">ภูมิภาคที่อนุญาต · {active.label}</div>
            {active.updated_at ? (
              <span className="chip" style={{ marginLeft: 'auto' }}>แก้ไขล่าสุด {thaiDateTime(active.updated_at)}</span>
            ) : null}
            {notice ? <span className="chip gold">{notice}</span> : null}
          </div>
          <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
            เลือกภูมิภาคที่องค์กรอนุญาตให้เก็บข้อมูลประเภทนี้ · การเพิ่มภูมิภาคคือการอนุมัติการโอนข้ามแดน
            และจะถูกบันทึกในร่องรอยการตรวจสอบทุกครั้ง
          </p>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            {regions.map((region) => {
              const on = draftRegions.includes(region.code)
              return (
                <button
                  key={region.code}
                  type="button"
                  className={`chip${on ? ' chip-on' : ''}`}
                  aria-pressed={on}
                  title={region.note}
                  onClick={() => toggleRegion(region.code)}
                  style={{ height: 32, padding: '0 12px', cursor: 'pointer' }}
                >
                  {region.label} · {region.code}
                </button>
              )
            })}
          </div>
          <div className="dim" style={{ fontSize: 10.5, lineHeight: 1.7, marginTop: 8 }}>
            {regions.filter((region) => draftRegions.includes(region.code))
              .map((region) => `${region.label}: ${region.zone} — ${region.note}`)
              .join(' · ') || 'ยังไม่ได้เลือกภูมิภาค ระบบจะปฏิเสธทุกภูมิภาค'}
          </div>

          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="residency-basis">ฐานกฎหมายที่บันทึกไว้</label>
            <input
              id="residency-basis"
              className="input"
              value={draftBasis}
              maxLength={300}
              disabled={save.isPending}
              placeholder={active.basis_default}
              onChange={(event) => { setDraftBasis(event.target.value); setNotice(null) }}
              style={{ width: '100%', height: 38 }}
            />
            <div className="dim" style={{ fontSize: 10.5, lineHeight: 1.7, marginTop: 6 }}>
              เว้นว่างเพื่อใช้ค่าเริ่มต้น: {active.basis_default}
            </div>
          </div>

          {draftRegions.length === 0 ? (
            <div className="form-error" style={{ marginTop: 10 }}>ต้องเลือกอย่างน้อยหนึ่งภูมิภาคก่อนบันทึก</div>
          ) : null}

          <div className="row" style={{ gap: 8, marginTop: 14 }}>
            <button
              className="btn btn-primary"
              disabled={!dirty || draftRegions.length === 0 || save.isPending}
              onClick={commit}
            >
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึกกฎ'}
            </button>
            <button className="btn btn-ghost" disabled={!dirty || save.isPending} onClick={resetDraft}>
              ยกเลิกการแก้ไข
            </button>
            <span className="dim" style={{ fontSize: 10.5 }}>
              {dirty ? 'ยังไม่ได้บันทึก' : 'ตรงกับที่บันทึกไว้'}
            </span>
          </div>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">ตรวจสอบการจัดเก็บ</div>
        <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 8 }}>
          ทดลองเลือกประเภทข้อมูลและภูมิภาคปลายทาง ระบบจะตอบว่าอนุญาตหรือปฏิเสธพร้อมเหตุผล
          โดยการตรวจสอบนี้ไม่บันทึกข้อมูลและไม่แก้กฎ
        </p>
        <div className="grid-2" style={{ marginTop: 14, gap: 14 }}>
          <div className="field">
            <label htmlFor="residency-check-class">ประเภทข้อมูล</label>
            <select
              id="residency-check-class"
              className="select"
              value={checkClass}
              onChange={(event) => { setCheckClass(event.target.value); setPlacement(null) }}
            >
              {classes.map((spec) => (
                <option key={spec.class} value={spec.class}>{spec.label} · {spec.class}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="residency-check-region">ภูมิภาคปลายทาง</label>
            <select
              id="residency-check-region"
              className="select"
              value={checkRegion}
              onChange={(event) => { setCheckRegion(event.target.value); setPlacement(null) }}
            >
              {regions.map((region) => (
                <option key={region.code} value={region.code}>{region.label} · {region.code}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="row" style={{ gap: 10, marginTop: 14 }}>
          <button
            className="btn btn-primary"
            disabled={check.isPending || !checkClass || !checkRegion}
            onClick={() => check.mutate({ class: checkClass, region: checkRegion })}
          >
            {check.isPending ? 'กำลังตรวจสอบ…' : 'ตรวจสอบ'}
          </button>
          <span className="dim" style={{ fontSize: 10.5 }}>ทดลองเท่านั้น ไม่มีการเขียนข้อมูล</span>
        </div>

        {check.error ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {check.error instanceof Error ? check.error.message : 'ตรวจสอบไม่สำเร็จ'}
          </div>
        ) : null}

        {placement ? (
          <div style={{
            marginTop: 16,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${placement.allowed ? 'rgba(46,125,100,0.45)' : 'rgba(166,58,74,0.45)'}`,
            background: placement.allowed ? 'rgba(46,125,100,0.06)' : 'rgba(166,58,74,0.06)',
          }}>
            <div className="row" style={{ gap: 8 }}>
              <span
                className="chip"
                style={{ color: placement.allowed ? 'var(--color-emerald-soft)' : 'var(--color-crimson)' }}
              >
                {placement.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
              </span>
              <span className="mono dim" style={{ fontSize: 11 }}>{placement.class} · {placement.region}</span>
              <span className="dim" style={{ fontSize: 10.5, marginLeft: 'auto' }}>{thaiDateTime(placement.checked_at)}</span>
            </div>
            <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.8, overflowWrap: 'anywhere' }}>{placement.reason}</div>
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">หลักการทำงาน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ค่าเริ่มต้นเก็บข้อมูลส่วนบุคคล ร่องรอยการตรวจสอบ และบัญชีไว้ในไทย ส่วนเอกสารและบัญชีเพิ่มสิงคโปร์ได้</li>
          <li>การโอนไปสหภาพยุโรปหรือสหรัฐอเมริกาต้องมีกฎที่บันทึกไว้และฐานตามมาตรา 28 หรือ 29 ทุกครั้ง</li>
          <li>ไม่มีกฎหรือไม่มีภูมิภาคที่อนุญาต หมายถึงปฏิเสธทุกภูมิภาค ไม่ใช่เปิดกว้าง</li>
          <li>ทุกการแก้ไขกฎบันทึกลงร่องรอยการตรวจสอบพร้อมภูมิภาคและฐานกฎหมายที่อนุมัติ</li>
        </ul>
      </div>
    </>
  )
}
